import * as d3 from 'd3'
import { useRef } from 'react'
import { useD3, type D3Svg, type D3Dims } from './useD3.js'
import { CHART_PALETTE } from './chartTokens.js'
import { useChartTooltip, type TooltipVariant } from './useChartTooltip.js'
import { ChartLegend } from './ChartLegend.js'
import styles from './DonutChart.module.css'

export interface DonutDatum {
  label: string
  value: number
  color?: string
}

interface DonutChartProps {
  data: DonutDatum[]
  /** 0–1 비율. 0이면 파이, 기본 0.6이면 도넛 */
  innerRadius?: number
  showTooltip?: boolean
  formatTooltip?: (datum: DonutDatum, percent: number) => string
  showLegend?: boolean
  tooltipVariant?: TooltipVariant
  className?: string
}

interface TooltipCtx {
  show: (x: number, y: number, text: string) => void
  hide: () => void
  format?: (datum: DonutDatum, percent: number) => string
}

function renderChart(
  svg: D3Svg,
  dims: D3Dims,
  data: DonutDatum[],
  innerRatio: number,
  tt?: TooltipCtx,
) {
  const { width, height } = dims
  const radius = Math.min(width, height) / 2 - 8
  if (radius <= 0) return

  const g = svg.append('g').attr('transform', `translate(${width / 2},${height / 2})`)

  const pie = d3.pie<DonutDatum>().value((d) => d.value).sort(null)
  const arc = d3.arc<d3.PieArcDatum<DonutDatum>>()
    .innerRadius(radius * innerRatio)
    .outerRadius(radius)

  const arcs = g.selectAll('.arc')
    .data(pie(data))
    .enter()
    .append('g')

  const paths = arcs.append('path')
    .attr('d', arc)
    .attr('fill', (d, i) => d.data.color ?? CHART_PALETTE[i % CHART_PALETTE.length])

  const total = d3.sum(data, (d) => d.value)
  if (total === 0) return

  const labelArc = d3.arc<d3.PieArcDatum<DonutDatum>>()
    .innerRadius(radius * 0.75)
    .outerRadius(radius * 0.75)

  arcs.filter((d) => d.data.value / total > 0.05)
    .append('text')
    .attr('transform', (d) => `translate(${labelArc.centroid(d)})`)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .attr('fill', '#fff')
    .attr('font-size', '12px')
    .attr('font-weight', '600')
    .text((d) => `${Math.round((d.data.value / total) * 100)}%`)

  if (tt) {
    paths
      .style('cursor', 'pointer')
      .on('mouseover mousemove', (event, d) => {
        const pct = Math.round((d.data.value / total) * 100)
        const text = tt.format?.(d.data, pct) ?? `${d.data.label}: ${d.data.value} (${pct}%)`
        const [mx, my] = d3.pointer(event, svg.node())
        tt.show(mx + 12, my - 10, text)
      })
      .on('mouseout', () => tt.hide())
  }
}

export function DonutChart({
  data,
  innerRadius = 0.6,
  showTooltip: enableTooltip = false,
  formatTooltip,
  showLegend = false,
  tooltipVariant,
  className,
}: DonutChartProps) {
  const { tooltipRef, show, hide, tooltipClassName } = useChartTooltip(tooltipVariant)
  const formatRef = useRef(formatTooltip)
  formatRef.current = formatTooltip

  const ref = useD3(
    (svg, dims) => {
      const tt = enableTooltip ? { show, hide, format: formatRef.current } : undefined
      renderChart(svg, dims, data, innerRadius, tt)
    },
    [data, innerRadius, enableTooltip],
  )

  const legendItems = showLegend
    ? data.map((d, i) => ({
        label: d.label,
        color: d.color ?? CHART_PALETTE[i % CHART_PALETTE.length],
        value: d.value,
      }))
    : undefined

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <svg ref={ref} className={styles.svg} />
      {enableTooltip && <div ref={tooltipRef} className={tooltipClassName} />}
      {legendItems && <ChartLegend items={legendItems} />}
    </div>
  )
}
